import { axiosClient } from '@/features/Client/Client'
import { AuthTokens } from '@/features/Auth/auth.types'
import { ApiRoutes } from '@/routes'

type NavigatorGetResponse = {
  rawId: Uint8Array;
  response: {
    authenticatorData: any;
    clientDataJSON: any;
    signature: any;
    attestationObject?: any;
  };
}

function toByteArray(base64String: string): ArrayBuffer {
  let input = base64String.replace(/-/g, '+').replace(/_/g, '/')
  const pad = input.length % 4
  if (pad) {
    input += new Array(5 - pad).join('=')
  }
  return Uint8Array.from(window.atob(input), (c) => c.charCodeAt(0))
}

function fromByteArray(buffer: Uint8Array) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64String = window.btoa(binary)
  return base64String.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export const passKeyService = {
  register: async () => {
    const res = await axiosClient.post(ApiRoutes.AUTH_LOGIN_PASSKEY_REGISTER_START, {})

    const settings = res.data

    const publicKey = {
      challenge: toByteArray(settings.challenge),
      rp: {
        name: settings.rp.name,
        id: settings.rp.id,
      },
      user: {
        name: settings.user.name,
        displayName: settings.user.displayName,
        id: toByteArray(settings.user.id),
      },
      pubKeyCredParams: settings.pubKeyCredParams,
      attestation: settings.attestation,
    }

    const newCredentialInfo = await navigator.credentials.create({
      publicKey,
    })

    const finishRequest = {
      credential: {
        id: newCredentialInfo.id,
        rawId: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).rawId),
        type: newCredentialInfo.type,
        response: {
          clientDataJSON: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).response.clientDataJSON),
          attestationObject: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).response.attestationObject),
        },
        clientExtensionResults: {},
      },
    }

    const response = await axiosClient.post(ApiRoutes.AUTH_LOGIN_PASSKEY_REGISTER_FINISH, finishRequest)
    return response.data
  },

  login: async (userId: string): Promise<AuthTokens | { error: string }> => {
    try {
      const startRes = await axiosClient.post(ApiRoutes.AUTH_LOGIN_PASSKEY_START, { userId })
      const startData = startRes.data

      const creds = []

      startData.publicKeyCredentialRequestOptions.allowCredentials.forEach(
        (cred: { id: string; type: string }) => {
          const result = {
            type: cred.type,
            id: toByteArray(cred.id),
          }
          creds.push(result)
        },
      )

      const publicKey = {
        challenge: toByteArray(startData.publicKeyCredentialRequestOptions.challenge),
        allowCredentials: creds,
      }

      const getAssertionResponse = await navigator.credentials.get({ publicKey })

      const finishRequest = {
        userId,
        credentials: {
          id: getAssertionResponse.id,
          rawId: fromByteArray((getAssertionResponse as unknown as NavigatorGetResponse).rawId),
          type: getAssertionResponse.type,
          response: {
            authenticatorData: fromByteArray((getAssertionResponse as unknown as NavigatorGetResponse).response.authenticatorData),
            clientDataJSON: fromByteArray((getAssertionResponse as unknown as NavigatorGetResponse).response.clientDataJSON),
            signature: fromByteArray((getAssertionResponse as unknown as NavigatorGetResponse).response.signature),
          },
          clientExtensionResults: {},
        },
      }

      const resFinish = await axiosClient.post(ApiRoutes.AUTH_LOGIN_PASSKEY_FINISH, { ...finishRequest })
      return resFinish.data
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        console.error('User interaction required or cancelled by user.')
      } if (e.name === 'NotFoundError') {
        console.error('Passkey identity not found on this device.')
      } else {
        console.error('Authentication failed:', e)
      }
      return { error: e.name }
    }
  },

  registerImmediately: async (userId: string): Promise<AuthTokens> => {
    const res = await axiosClient.post(ApiRoutes.PUB_PASSKEY_START, {
      userId,
    })

    const settings = res.data

    const publicKey = {
      challenge: toByteArray(settings.challenge),
      rp: {
        name: settings.rp.name,
        id: settings.rp.id,
      },
      user: {
        name: settings.user.name,
        displayName: settings.user.displayName,
        id: toByteArray(settings.user.id),
      },
      pubKeyCredParams: settings.pubKeyCredParams,
      attestation: settings.attestation,
    }

    const newCredentialInfo = await navigator.credentials.create({
      publicKey,
    })

    const finishRequest = {
      userId,
      finishRegistrationRequest: {
        credential: {
          id: newCredentialInfo.id,
          rawId: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).rawId),
          type: newCredentialInfo.type,
          response: {
            clientDataJSON: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).response.clientDataJSON),
            attestationObject: fromByteArray((newCredentialInfo as unknown as NavigatorGetResponse).response.attestationObject),
          },
          clientExtensionResults: {},
        },
      },
    }

    const response = await axiosClient.post(ApiRoutes.PUB_PASSKEY_FINISH, finishRequest)
    return response.data
  },
}
